import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
  Sheet,
  Button,
  Toolbar,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Wallet,
  User,
  Hash,
  CreditCard,
  Calendar,
  X,
  AlertTriangle,
  Banknote,
  Copy,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

type WithdrawalStatus = 'pending' | 'processing' | 'completed' | 'rejected'

interface Withdrawal {
  id: string
  user: string
  amount: number
  method: string
  address: string
  date: Date
  status: WithdrawalStatus
  reason?: string
}

// --- Mock Data ---

const mockWithdrawals: Withdrawal[] = [
  { id: 'W001', user: '王大明', amount: 5000, method: 'USDT TRC-20', address: 'TXyz...abc', date: new Date(Date.now() - 1000 * 60 * 30), status: 'pending' },
  { id: 'W002', user: 'David Chen', amount: 10000, method: 'USDT TRC-20', address: 'TAbc...xyz', date: new Date(Date.now() - 1000 * 60 * 60), status: 'pending' },
  { id: 'W003', user: 'Sarah Lin', amount: 20000, method: 'USDT TRC-20', address: 'TDef...ghi', date: new Date(Date.now() - 1000 * 60 * 60 * 3), status: 'processing' },
  { id: 'W004', user: 'Mike Wang', amount: 8000, method: 'USDT TRC-20', address: 'TGhi...jkl', date: new Date(Date.now() - 1000 * 60 * 60 * 24), status: 'completed' },
  { id: 'W005', user: '小美', amount: 15000, method: 'USDT TRC-20', address: 'TJkl...mno', date: new Date(Date.now() - 1000 * 60 * 60 * 48), status: 'completed' },
  { id: 'W006', user: 'Alex Kim', amount: 50000, method: 'USDT TRC-20', address: 'TMno...pqr', date: new Date(Date.now() - 1000 * 60 * 60 * 72), status: 'rejected', reason: '餘額不足' },
]

// --- Tab definitions ---

type TabKey = 'pending' | 'processing' | 'completed' | 'rejected'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'pending', label: '待審核' },
  { key: 'processing', label: '處理中' },
  { key: 'completed', label: '已完成' },
  { key: 'rejected', label: '已拒絕' },
]

// --- Helpers ---

function getStatusConfig(status: WithdrawalStatus) {
  switch (status) {
    case 'pending':
      return { label: '待審核', color: '#FF9500', bgClass: 'bg-[#FF9500]/15', Icon: Clock }
    case 'processing':
      return { label: '處理中', color: '#007AFF', bgClass: 'bg-[#007AFF]/15', Icon: Loader2 }
    case 'completed':
      return { label: '已完成', color: '#34C759', bgClass: 'bg-[#34C759]/15', Icon: CheckCircle2 }
    case 'rejected':
      return { label: '已拒絕', color: '#FF3B30', bgClass: 'bg-[#FF3B30]/15', Icon: XCircle }
  }
}

function StatusBadge({ status }: { status: WithdrawalStatus }) {
  const config = getStatusConfig(status)
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
        config.bgClass,
      )}
      style={{ color: config.color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full mr-1"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
  iconColor = '#8E8E93',
  valueColor,
}: {
  icon: React.ElementType
  label: string
  value: string
  iconColor?: string
  valueColor?: string
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2.5 text-[#8E8E93]">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <span className="text-[14px]">{label}</span>
      </div>
      <span
        className="text-[14px] font-medium"
        style={{ color: valueColor || '#000' }}
      >
        {value}
      </span>
    </div>
  )
}

// --- Component ---

export function AdminWithdrawalsScreen() {
  const [activeTab, setActiveTab] = useState<TabKey>('pending')
  const [selectedItem, setSelectedItem] = useState<Withdrawal | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectTarget, setRejectTarget] = useState<string | null>(null)

  // Filter withdrawals by tab
  const filteredWithdrawals = useMemo(() => {
    return mockWithdrawals.filter((w) => w.status === activeTab)
  }, [activeTab])

  const handleItemTap = (item: Withdrawal) => {
    setSelectedItem(item)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
    setTimeout(() => setSelectedItem(null), 300)
  }

  const handleApprove = () => {
    if (selectedItem) {
      console.log(`[Admin] Approved withdrawal ${selectedItem.id} for ${selectedItem.user}`)
      handleCloseSheet()
    }
  }

  const handleRejectClick = () => {
    if (selectedItem) {
      setRejectTarget(selectedItem.id)
      setRejectReason('')
      setRejectDialogOpen(true)
    }
  }

  const handleRejectConfirm = () => {
    if (rejectTarget) {
      console.log(`[Admin] Rejected withdrawal ${rejectTarget}, reason: ${rejectReason || '未提供原因'}`)
      setRejectDialogOpen(false)
      setRejectTarget(null)
      setRejectReason('')
      handleCloseSheet()
    }
  }

  const handleRejectCancel = () => {
    setRejectDialogOpen(false)
    setRejectTarget(null)
    setRejectReason('')
  }

  // Summary stats
  const stats = useMemo(() => {
    const pending = mockWithdrawals.filter((w) => w.status === 'pending')
    const pendingTotal = pending.reduce((sum, w) => sum + w.amount, 0)
    return {
      pendingCount: pending.length,
      pendingTotal,
      todayProcessed: mockWithdrawals.filter((w) => w.status === 'completed').length,
    }
  }, [])

  return (
    <>
      <Navbar title="提款管理" />

      {/* Summary stats */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center mx-auto mb-1">
              <Clock className="w-4 h-4 text-[#FF9500]" />
            </div>
            <p className="text-lg font-bold text-black">{stats.pendingCount}</p>
            <p className="text-[11px] text-[#8E8E93]">待審核</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center mx-auto mb-1">
              <Banknote className="w-4 h-4 text-[#007AFF]" />
            </div>
            <p className="text-lg font-bold text-black">{formatCurrency(stats.pendingTotal)}</p>
            <p className="text-[11px] text-[#8E8E93]">待審金額</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center mx-auto mb-1">
              <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
            </div>
            <p className="text-lg font-bold text-black">{stats.todayProcessed}</p>
            <p className="text-[11px] text-[#8E8E93]">已完成</p>
          </div>
        </div>
      </Block>

      {/* Segmented tabs */}
      <div className="px-4 py-3">
        <Segmented strong>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </div>

      {/* Withdrawal count */}
      <BlockTitle className="!mt-1">
        {filteredWithdrawals.length} 筆提款
      </BlockTitle>

      {/* Withdrawal list */}
      {filteredWithdrawals.length > 0 ? (
        <List strongIos insetIos>
          {filteredWithdrawals.map((item) => {
            const config = getStatusConfig(item.status)
            return (
              <ListItem
                key={item.id}
                link
                chevron={false}
                onClick={() => handleItemTap(item)}
                media={
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', config.bgClass)}>
                    <Wallet className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                }
                title={
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.user}</span>
                  </div>
                }
                subtitle={
                  <span className="text-[#8E8E93] text-[12px]">
                    {item.method} &middot; {formatRelativeTime(item.date)}
                  </span>
                }
                after={
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[15px] font-semibold text-[#FF3B30] tabular-nums">
                      -{formatCurrency(item.amount)}
                    </span>
                    <StatusBadge status={item.status} />
                  </div>
                }
              />
            )
          })}
        </List>
      ) : (
        <Block className="text-center py-12">
          <Banknote className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">
            沒有{tabs.find((t) => t.key === activeTab)?.label}的提款
          </p>
        </Block>
      )}

      {/* Bottom padding */}
      <div className="h-8" />

      {/* Detail Sheet */}
      <Sheet
        opened={sheetOpen}
        onBackdropClick={handleCloseSheet}
        className="pb-safe"
      >
        {selectedItem && (
          <div className="flex flex-col max-h-[85vh]">
            {/* Sheet header */}
            <Toolbar top>
              <div className="flex items-center justify-between w-full px-1">
                <span className="text-[17px] font-semibold">
                  提款詳情
                </span>
                <button
                  onClick={handleCloseSheet}
                  className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-[#8E8E93]" />
                </button>
              </div>
            </Toolbar>

            {/* Sheet content */}
            <div className="overflow-y-auto flex-1 px-4 pt-2 pb-4">
              {/* Amount highlight */}
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-2xl bg-[#FF3B30]/15 flex items-center justify-center mx-auto mb-3">
                  <Banknote className="w-7 h-7 text-[#FF3B30]" />
                </div>
                <p className="text-[32px] font-bold text-[#FF3B30] tabular-nums">
                  -{formatCurrency(selectedItem.amount)}
                </p>
                <StatusBadge status={selectedItem.status} />
              </div>

              {/* User info */}
              <BlockTitle className="!px-0 !mt-2 !mb-2">用戶資訊</BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={User}
                  label="用戶名稱"
                  value={selectedItem.user}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={Hash}
                  label="提款編號"
                  value={selectedItem.id}
                  iconColor="#8E8E93"
                />
              </div>

              {/* Withdrawal details */}
              <BlockTitle className="!px-0 !mt-4 !mb-2">提款資訊</BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={CreditCard}
                  label="提款方式"
                  value={selectedItem.method}
                  iconColor="#AF52DE"
                />
                <DetailRow
                  icon={Wallet}
                  label="錢包地址"
                  value={selectedItem.address}
                  iconColor="#FF9500"
                />
                <DetailRow
                  icon={Banknote}
                  label="提款金額"
                  value={formatCurrency(selectedItem.amount)}
                  iconColor="#FF3B30"
                  valueColor="#FF3B30"
                />
                <DetailRow
                  icon={Calendar}
                  label="申請時間"
                  value={selectedItem.date.toLocaleString('zh-TW', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  iconColor="#8E8E93"
                />
                <DetailRow
                  icon={CheckCircle2}
                  label="狀態"
                  value={getStatusConfig(selectedItem.status).label}
                  iconColor={getStatusConfig(selectedItem.status).color}
                  valueColor={getStatusConfig(selectedItem.status).color}
                />
              </div>

              {/* Rejection reason if rejected */}
              {selectedItem.status === 'rejected' && selectedItem.reason && (
                <>
                  <BlockTitle className="!px-0 !mt-4 !mb-2">拒絕原因</BlockTitle>
                  <div className="bg-[#FF3B30]/10 rounded-xl px-4 py-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                    <span className="text-[14px] text-[#FF3B30]">{selectedItem.reason}</span>
                  </div>
                </>
              )}

              {/* Copy address button */}
              <div className="mt-4">
                <Button
                  large
                  outline
                  onClick={() => {
                    navigator.clipboard?.writeText(selectedItem.address)
                    console.log('[Admin] Copied wallet address')
                  }}
                >
                  <Copy className="w-4 h-4 mr-1.5 inline" />
                  複製錢包地址
                </Button>
              </div>

              {/* Action buttons for pending items */}
              {selectedItem.status === 'pending' && (
                <div className="flex gap-3 mt-3">
                  <Button
                    large
                    className="flex-1"
                    onClick={handleApprove}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5 inline" />
                    批准
                  </Button>
                  <Button
                    large
                    outline
                    className="flex-1 !text-[#FF3B30] !border-[#FF3B30]"
                    onClick={handleRejectClick}
                  >
                    <XCircle className="w-4 h-4 mr-1.5 inline" />
                    拒絕
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Sheet>

      {/* Rejection reason dialog */}
      <Dialog
        opened={rejectDialogOpen}
        onBackdropClick={handleRejectCancel}
        title="拒絕提款"
        content={
          <div className="mt-2">
            <p className="text-[13px] text-[#8E8E93] mb-3">
              請輸入拒絕原因：
            </p>
            <input
              type="text"
              className="w-full border border-[#C7C7CC] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#007AFF]"
              placeholder="例：餘額不足、資料不符..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        }
        buttons={
          <>
            <DialogButton onClick={handleRejectCancel}>
              取消
            </DialogButton>
            <DialogButton strong onClick={handleRejectConfirm}>
              確認拒絕
            </DialogButton>
          </>
        }
      />
    </>
  )
}
